// pelanggan.repo — CRUD pelanggan + riwayat servis. RLS men-scope per cabang.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import type { Customer } from '@core/types';

export interface WaHist { old: string; new: string; by: string; at: string }
export interface CustInput {
  nama: string; kat: 'User' | 'Toko'; telp: string; wa: string;
  lahir?: string | null; email?: string; kota?: string; alamat?: string; sumber?: string; catatan?: string;
  outlet?: string | null; wa_history?: WaHist[];
}
export interface CustHist { no: string; status: string; unit: string; tgl: string }
export interface CustDetail { customer: Customer; tickets: CustHist[]; waHistory: WaHist[] }

/** Nomor WA + riwayat perubahan saat ini — untuk deteksi ganti-nomor di saveCust. */
export async function getWaInfo(id: number): Promise<{ wa: string | null; wa_history: WaHist[] }> {
  const { data, error } = await supabase().from('customers').select('wa,wa_history').eq('id', id).single();
  if (error) throw new Error(error.message);
  const r = data as { wa: string | null; wa_history: WaHist[] | null };
  return { wa: r.wa, wa_history: r.wa_history ?? [] };
}

export async function listCustomers(): Promise<Customer[]> {
  return fetchAllPaged<Customer>((dari, sampai) => supabase()
    .from('customers').select('id,nama,kat,telp,wa,lahir,email,kota,alamat,sumber,outlet')
    .order('nama').range(dari, sampai));
}

export async function findByWA(wa: string): Promise<Customer | null> {
  if (!/^\d+$/.test(wa)) return null;
  const { data, error } = await supabase()
    .from('customers').select('id,nama,wa,telp').or(`wa.eq.${wa},telp.eq.${wa}`).limit(1);
  if (error) throw new Error(error.message);
  return data && data[0] ? (data[0] as Customer) : null;
}

export async function createCustomer(d: CustInput): Promise<number> {
  const { data, error } = await supabase().from('customers').insert(d).select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: number }).id;
}
export async function updateCustomer(id: number, d: CustInput): Promise<void> {
  const { error } = await supabase().from('customers').update(d).eq('id', id);
  if (error) throw new Error(error.message);
}
export async function removeCustomer(id: number): Promise<void> {
  const { error } = await supabase().from('customers').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Jumlah servis per pelanggan (satu query, RLS-scoped) → Map id→count. Port "Total Servis" v0.18. */
export async function ticketCounts(): Promise<Map<number, number>> {
  const data = await fetchAllPaged<{ customer_id: number | null }>((a, b) =>
    supabase().from('tickets').select('customer_id').order('no').range(a, b));
  const m = new Map<number, number>();
  for (const r of data) {
    if (r.customer_id != null) m.set(r.customer_id, (m.get(r.customer_id) ?? 0) + 1);
  }
  return m;
}

export async function ticketCount(custId: number): Promise<number> {
  const { count, error } = await supabase()
    .from('tickets').select('no', { count: 'exact', head: true }).eq('customer_id', custId);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export async function detail(id: number): Promise<CustDetail> {
  const { data: c, error } = await supabase()
    .from('customers').select('id,nama,kat,telp,wa,lahir,email,kota,alamat,sumber,wa_history').eq('id', id).single();
  if (error) throw new Error(error.message);
  const { data: tks } = await supabase()
    .from('tickets').select('no,status,unit,tgl_iso').eq('customer_id', id).order('tgl_iso', { ascending: false });
  const rows = (tks ?? []) as { no: string; status: string; unit: string | null; tgl_iso: string | null }[];
  return {
    customer: c as Customer,
    tickets: rows.map((t) => ({ no: t.no, status: t.status, unit: t.unit ?? '—', tgl: t.tgl_iso ?? '—' })),
    waHistory: ((c as { wa_history?: WaHist[] | null }).wa_history ?? []),
  };
}
